<template>
  <v-dialog
    v-model="dialogStatus"
    max-width="800"
    persistent
  >
    <v-card
      class="d-flex flex-column"
    >
      <v-app-bar
        color="cyan"
        dark
        flat
      >
        <v-toolbar-title class="text-h5">
          <v-icon class="mr-2">
            link
          </v-icon>
          Link Party
        </v-toolbar-title>

        <v-spacer />

        <v-btn
          icon
          @click="closeDialog"
        >
          <v-icon>close</v-icon>
        </v-btn>
      </v-app-bar>

      <v-card-text
        class="pa-0"
      >
        <div
          class="d-flex flex-column"
        >
          <div
            class="teal lighten-4"
          >
            <v-text-field
              v-model="searchTerm"
              class="my-3 mx-2 px-2"
              clear-icon="mdi-close-circle"
              clearable
              hide-details
              label="Search Parties"
              x-placeholder="Text to Search"
              x-persistent-placeholder
              prepend-icon="mdi-magnify"
            />
          </div>

          <v-data-table
            v-model="selectedParty"
            class="search-results"
            fixed-header
            :headers="tableHeaders"
            height="65vh"
            hide-default-footer
            :items="results"
            :items-per-page="pagination.itemsPerPage"
            :loading="isLoading"
            loading-text="Fetching results..."
            show-select
            single-select
            sort-by="first_name"
            @update:options="onDataTableOptionsUpdated"
          />
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-data-footer
          class="search-pagination"
          :items-per-page-options="availablePageSizes"
          :options="footerOptions"
          :pagination="footerPagination"
          @update:options="onFooterOptionsUpdated"
        />

        <v-spacer />

        <v-btn
          text
          color="secondary"
          @click="closeDialog"
        >
          Cancel
        </v-btn>

        <v-btn
          color="primary"
          :disabled="!selectedParty.length"
          @click="onLinkParty"
        >
          Link Party
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { alertHandler } from '@/alertHandler'
import { debounce } from 'lodash'
import { mapActions } from 'vuex'

import { PARTY_NAMESPACE } from '@/store/party'

export default {
  name: 'DialogLinkParty',
  inheritAttrs: false,

  props: {
    editedPartyId: {
      type: Number,
      default: null,
    },

    status: {
      type: Boolean,
      required: true,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      availablePageSizes: [
        20,
        50,
        100,
      ],

      isLoading: true,
      linkableParties: [],

      pagination: {
        itemsPerPage: 20,
        page: 1,
        pageCount: 0,
        pageStart: 0,
        pageStop: 0,
        sortBy: [],
        sortDesc: [],
      },

      searchTerm: '',
      selectedParty: [],

      tableHeaders: [
        { text: 'First name', value: 'first_name' },
        { text: 'Middle name', value: 'middle_name' },
        { text: 'Last name', value: 'last_name' },
        { text: 'Date of birth', value: 'dob' },
      ],
    }
  },

  computed: {
    dialogStatus () {
      return this.status
    },

    footerOptions () {
      return {
        itemsPerPage: this.pagination.itemsPerPage,
        page: this.pagination.page,
      }
    },

    footerPagination () {
      return {
        itemsLength: this.pagination.totalItems,
        itemsPerPage: this.pagination.itemsPerPage,
        page: this.pagination.page,
        pageCount: this.pagination.pageCount,
        pageStart: this.pagination.pageStart - 1,
        pageStop: this.pagination.pageStop,
      }
    },

    results () {
      return this.isLoading ? [] : this.linkableParties
    },
  },

  watch: {
    searchTerm () {
      this.isLoading = true
      this.debounceSearch()
    },

    status (newVal, oldVal) {
      if (newVal !== oldVal && newVal) this.reset()
    },
  },

  methods: {
    closeDialog () {
      this.$emit('close')
    },

    debounceSearch: debounce(function () {
      this.pagination.page = 1
      this.fetchLinkableParties()
    }, 500),

    fetchLinkableParties () {
      this.isLoading = true
      this.linkableParties.splice(0)

      this.getLinkableParties({
        itemsPerPage: this.pagination.itemsPerPage,
        page: this.pagination.page,
        partyId: this.editedPartyId,
        searchTerm: this.searchTerm,
        sortBy: this.pagination.sortBy,
        sortDesc: this.pagination.sortDesc,
      }).
        then(data => {
          this.linkableParties = data.items

          this.pagination = {
            itemsPerPage: data.itemsPerPage,
            page: data.page,
            pageCount: data.pageCount,
            pageStart: data.pageStart,
            pageStop: data.pageStop,
            sortBy: data.sortBy,
            sortDesc: data.sortDesc,
            totalItems: data.totalItems,
          }
        }).
        finally(_ => {
          this.isLoading = false
        })
    },

    onDataTableOptionsUpdated (options) {
      this.pagination.sortBy = options.sortBy.length ? options.sortBy[0] : ''
      this.pagination.sortDesc = options.sortDesc ? options.sortDesc[0] : null
      this.fetchLinkableParties()
    },

    onFooterOptionsUpdated (options) {
      this.pagination.itemsPerPage = options.itemsPerPage
      this.pagination.page = options.page
      this.fetchLinkableParties()
    },

    onLinkParty () {
      if (!this.selectedParty?.length) return
      this.$emit('start-link', this.selectedParty[0].id)
    },

    reset () {
      this.isLoading = true

      this.searchTerm = ''
      this.selected = null
      this.linkableParties.splice(0)

      this.pagination = {
        itemsPerPage: 20,
        page: 0,
        pageCount: 0,
        pageStart: 0,
        pageStop: 0,
        sortBy: '',
        sortDesc: null,
        totalItems: 0,
      }
    },

    ...mapActions(PARTY_NAMESPACE, [
      'getLinkableParties',
    ]),
  },
}
</script>

<style scoped>
.search-results {
  .v-data-table__wrapper {
    padding-left: 16px;
    padding-right: 16px;
  }
}

.search-pagination {
  .v-data-footer__select {
    margin-left: 0;
  }
}
</style>
